/* ==========================================================================
   Ida Louise Boutique — theme.js
   Plain JavaScript, no libraries. Each feature is a small custom element or
   a function, so the Theme Editor can reload any section safely.
   ========================================================================== */
(function () {
  'use strict';

  var ILB = (window.ILB = window.ILB || {});
  var routes = ILB.routes || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function parseHTML(html) { return new DOMParser().parseFromString(html, 'text/html'); }
  function formatMoney(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    var value = cents / 100;
    var opts = { style: 'currency', currency: currency };
    if (value % 1 === 0) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
    try { return new Intl.NumberFormat('en-US', opts).format(value); } catch (e) { return '$' + value; }
  }
  ILB.formatMoney = formatMoney;

  /* ---------- tracking ----------
     Every [data-track] click becomes a Shopify customer event (for a custom
     pixel), a dataLayer push, and a GA4 event when a GA4 ID is set. */
  function track(name, detail) {
    detail = detail || {};
    detail.page_type = document.body.className.split(' ')[0].replace('tpl-', '');
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, detail)); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', name, detail); } catch (e) {}
  }
  ILB.track = track;
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-track]');
    if (!el) return;
    track(el.getAttribute('data-track'), {
      placement: el.getAttribute('data-track-placement') || '',
      product: el.getAttribute('data-track-product') || '',
      link_url: el.getAttribute('href') || ''
    });
  });

  /* ---------- reveal on scroll + count-up ---------- */
  var revealIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      revealIO.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  function countUp(el) {
    var raw = el.getAttribute('data-count-up') || el.textContent;
    var m = raw.match(/[\d.,]+/);
    if (!m || reduceMotion) return;
    var numStr = m[0];
    var target = parseFloat(numStr.replace(/,/g, ''));
    if (!isFinite(target) || target < 10) return;
    var decimals = (numStr.split('.')[1] || '').length;
    var prefix = raw.slice(0, m.index), suffix = raw.slice(m.index + numStr.length);
    var start = null, dur = 1700;
    function frame(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      var val = target * eased;
      el.textContent = prefix + val.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
      if (p < 1) requestAnimationFrame(frame); else el.textContent = raw;
    }
    requestAnimationFrame(frame);
  }
  var countIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      countUp(entry.target);
      countIO.unobserve(entry.target);
    });
  }, { threshold: 0.6 }) : null;

  function initReveal(root) {
    root = root || document;
    if (!revealIO) { $$('[data-reveal]', root).forEach(function (el) { el.classList.add('is-in'); }); return; }
    $$('[data-reveal], .lake, [data-inview]', root).forEach(function (el) { if (!el.classList.contains('is-in')) revealIO.observe(el); });
    if (countIO) $$('[data-count-up]', root).forEach(function (el) { countIO.observe(el); });
  }
  ILB.initReveal = initReveal;

  /* ---------- body scroll lock ---------- */
  var lockCount = 0;
  function lock() { lockCount++; document.body.classList.add('is-locked'); }
  function unlock() { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('is-locked'); }

  /* ---------- base drawer (menu, cart, search) ---------- */
  class ILBDrawer extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.addEventListener('click', function (e) { if (e.target.closest('[data-close]')) self.close(); });
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.close();
        if (e.key === 'Tab') self.trap(e);
      });
    }
    get isOpen() { return this.classList.contains('is-open'); }
    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lock();
      var focusTarget = this.querySelector('[data-search-input]') || this.querySelector('[role="dialog"]');
      setTimeout(function () { if (focusTarget) focusTarget.focus({ preventScroll: true }); }, 60);
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
    }
    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlock();
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
    trap(e) {
      var f = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', this)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  class MenuDrawer extends ILBDrawer {}
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  /* ---------- cart drawer ---------- */
  class CartDrawer extends ILBDrawer {
    connectedCallback() {
      super.connectedCallback();
      var self = this;
      this.addEventListener('click', function (e) {
        var b = e.target.closest('[data-qty]');
        if (!b) return;
        e.preventDefault();
        self.change(b.getAttribute('data-key'), parseInt(b.getAttribute('data-qty'), 10));
      });
      this.addEventListener('change', function (e) {
        var input = e.target.closest('input[data-key]');
        if (!input) return;
        self.change(input.getAttribute('data-key'), Math.max(0, parseInt(input.value, 10) || 0));
      });
      updateCount(this.getAttribute('data-count'));
    }
    change(key, qty) {
      var self = this;
      var body = this.querySelector('[data-cart-body]');
      if (body) body.classList.add('is-updating');
      fetch((routes.cartChange || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty, sections: 'cart-drawer', sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.sections && data.sections['cart-drawer']) self.render(data.sections['cart-drawer']);
          else if (body) body.classList.remove('is-updating');
        })
        .catch(function () { if (body) body.classList.remove('is-updating'); });
    }
    render(html) {
      var doc = parseHTML(html);
      var fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;
      var wasOpen = this.isOpen;
      this.innerHTML = fresh.innerHTML;
      this.setAttribute('data-count', fresh.getAttribute('data-count'));
      updateCount(fresh.getAttribute('data-count'));
      if (wasOpen) { var p = this.querySelector('[role="dialog"]'); if (p) p.focus({ preventScroll: true }); }
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  function updateCount(n) {
    n = parseInt(n, 10) || 0;
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = n > 0 ? n : '';
      el.setAttribute('aria-label', n + (n === 1 ? ' item' : ' items'));
    });
  }

  /* ---------- Ida Louise line index (search + recommendations) ----------
     Shopify's own search and recommendations rank the whole store, which is
     mostly designer pieces, so brand results came back thin. This loads the
     tag-filtered line once (sections/brand-index.liquid) and works from that. */
  function fold(str) {
    str = String(str || '').toLowerCase();
    if (str.normalize) str = str.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return str.replace(/[^a-z0-9]+/g, ' ').trim();
  }
  var BrandIndex = {
    promise: null,
    items: null,
    url: function (tag) {
      return (routes.root || '/').replace(/\/?$/, '/') + 'collections/all/' + encodeURIComponent(tag) + '?section_id=brand-index';
    },
    load: function () {
      if (this.promise) return this.promise;
      var self = this, found = [], seen = {};
      var tags = [ILB.brandTag || 'ida-louise-brand'];
      if (ILB.extraTag && ILB.extraTag !== tags[0]) tags.push(ILB.extraTag);
      function page(url, depth) {
        return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.text() : ''; }).then(function (html) {
          if (!html) return;
          var root = parseHTML(html).querySelector('[data-brand-index]');
          if (!root) return;
          if (!self.arrow) { var tpl = root.querySelector('template[data-arrow]'); if (tpl) self.arrow = tpl.innerHTML; }
          $$('li[data-handle]', root).forEach(function (li) {
            var h = li.getAttribute('data-handle');
            var t = li.getAttribute('data-tags') || '';
            if (seen[h] || !tags.some(function (tag) { return t.indexOf(' ' + String(tag).toLowerCase() + ' ') > -1; })) return;
            seen[h] = 1;
            found.push({ handle: h, tags: li.getAttribute('data-tags') || '', title: fold(li.getAttribute('data-title')), text: fold(li.getAttribute('data-s')), el: li, order: found.length });
          });
          var next = root.getAttribute('data-next');
          if (next && depth < 10) {
            if (next.indexOf('section_id=') === -1) next += (next.indexOf('?') > -1 ? '&' : '?') + 'section_id=brand-index';
            return page(next, depth + 1);
          }
        });
      }
      this.promise = Promise.all(tags.map(function (t) { return page(self.url(t), 0); })).then(function () {
        if (!found.length) throw new Error('empty index');
        self.items = found;
        return found;
      });
      this.promise.catch(function () { self.promise = null; });
      return this.promise;
    },
    search: function (q) {
      var words = fold(q).split(' ').filter(function (w) { return w.length > 1; });
      if (!words.length || !this.items) return [];
      var forms = words.map(function (w) {
        var f = [w];
        if (w.length > 4 && /es$/.test(w)) f.push(w.slice(0, -2));
        if (w.length > 3 && /s$/.test(w)) f.push(w.slice(0, -1));
        return f;
      });
      var hits = [];
      this.items.forEach(function (it) {
        var score = 0;
        for (var i = 0; i < forms.length; i++) {
          var inTitle = false, inText = false;
          for (var j = 0; j < forms[i].length; j++) {
            if (it.title.indexOf(forms[i][j]) > -1) inTitle = true;
            if (it.text.indexOf(forms[i][j]) > -1) inText = true;
          }
          if (!inTitle && !inText) return;
          score += inTitle ? 3 : 1;
        }
        hits.push({ it: it, score: score });
      });
      hits.sort(function (a, b) { return b.score - a.score || a.it.order - b.it.order; });
      return hits.map(function (h) { return h.it; });
    }
  };
  ILB.brandIndex = BrandIndex;
  function escapeHTML(str) {
    return String(str).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }
  function searchURL(q) {
    return (routes.search || '/search') + '?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last';
  }

  /* ---------- search overlay with predictive results ---------- */
  class SearchOverlay extends ILBDrawer {
    connectedCallback() {
      super.connectedCallback();
      var self = this;
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.initial = this.results ? this.results.innerHTML : '';
      if (!this.input) return;
      this.input.addEventListener('input', debounce(function () { self.search(self.input.value.trim()); }, 220));
      this.input.addEventListener('focus', function () { BrandIndex.load().catch(function () {}); }, { once: true });
    }
    search(q) {
      var self = this;
      if (!q) { this.results.innerHTML = this.initial; return; }
      this.lastQ = q;
      BrandIndex.load().then(function (items) {
        if (self.lastQ !== q) return;
        var hits = BrandIndex.search(q).slice(0, 8);
        var html;
        if (hits.length) {
          html = '<div class="predictive" data-predictive><div class="shead shead--split" style="margin-bottom:0"><p class="eyebrow">Products</p>' +
            '<div class="shead__aside"><a class="link-arrow" href="' + searchURL(q) + '">See all results ' + (BrandIndex.arrow || '') + '</a></div></div>' +
            '<ul class="predictive__grid list-reset" role="list">' + hits.map(function (h) { return h.el.outerHTML; }).join('') + '</ul></div>';
        } else {
          html = '<div class="predictive" data-predictive><p class="predictive__empty">Nothing in the Ida Louise line matches &ldquo;' + escapeHTML(q) +
            '&rdquo;. Try a scent, like Sugared Pumpkin, or a piece, like linen.</p></div>';
        }
        self.results.innerHTML = html;
        track('search', { search_term: q });
      }).catch(function () { self.predictive(q); });
    }
    predictive(q) {
      var self = this;
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = 'AbortController' in window ? new AbortController() : null;
      var url = (routes.predictive || '/search/suggest') + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last&section_id=predictive-search';
      fetch(url, { signal: this.ctrl ? this.ctrl.signal : undefined })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = parseHTML(html);
          var el = doc.querySelector('[data-predictive]');
          self.results.innerHTML = el ? el.outerHTML : '';
          track('search', { search_term: q });
        })
        .catch(function () {});
    }
  }
  if (!customElements.get('search-overlay')) customElements.define('search-overlay', SearchOverlay);

  /* ---------- header ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.section = this.closest('.hdr-section') || this.parentElement;
      this.hideOnScroll = this.getAttribute('data-hide-on-scroll') === 'true';
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      // Mega menu
      this.megaBtn = this.querySelector('[data-mega]');
      this.mega = this.querySelector('[data-mega-panel]');
      if (this.megaBtn && this.mega) {
        var closeTimer;
        var openMega = function () { clearTimeout(closeTimer); self.setMega(true); };
        var closeMega = function () { closeTimer = setTimeout(function () { self.setMega(false); }, 160); };
        this.megaBtn.addEventListener('click', function () { self.setMega(self.megaBtn.getAttribute('aria-expanded') !== 'true'); });
        if (window.matchMedia('(hover: hover)').matches) {
          this.megaBtn.addEventListener('mouseenter', openMega);
          this.megaBtn.addEventListener('mouseleave', closeMega);
          this.mega.addEventListener('mouseenter', openMega);
          this.mega.addEventListener('mouseleave', closeMega);
        }
        this.mega.addEventListener('focusout', function (e) { if (!self.contains(e.relatedTarget)) self.setMega(false); });
        document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && self.megaBtn.getAttribute('aria-expanded') === 'true') { self.setMega(false); self.megaBtn.focus(); } });
        document.addEventListener('click', function (e) { if (!self.contains(e.target)) self.setMega(false); });
      }

      this.addEventListener('click', function (e) {
        var s = e.target.closest('[data-open-search]');
        if (s) { var so = $('#SearchOverlay'); if (so) { e.preventDefault(); so.open(s); } return; }
        var c = e.target.closest('[data-open-cart]');
        if (c && !document.body.classList.contains('tpl-cart')) { var cd = $('#CartDrawer'); if (cd) { e.preventDefault(); cd.open(c); } return; }
        var m = e.target.closest('[data-open-menu]');
        if (m) { var md = $('#MenuDrawer'); if (md) { e.preventDefault(); md.open(m); } }
      });
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    setMega(open) {
      if (!this.mega) return;
      this.mega.classList.toggle('is-open', open);
      this.megaBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.classList.toggle('is-open', open);
    }
    onScroll() {
      var y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 8);
      if (this.hideOnScroll && this.section) {
        var goingDown = y > this.lastY;
        var hide = goingDown && y > 420 && !this.classList.contains('is-open');
        if (Math.abs(y - this.lastY) > 4) {
          this.section.classList.toggle('is-hidden', hide);
          document.documentElement.classList.toggle('hdr-hidden', hide);
        }
      }
      this.lastY = y;
    }
  }
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

  /* ---------- hero load-in ---------- */
  class HeroBanner extends HTMLElement {
    connectedCallback() {
      var self = this;
      var img = this.querySelector('.hero__media img');
      var done = function () { requestAnimationFrame(function () { self.classList.add('is-loaded'); }); };
      if (!img || img.complete) done();
      else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); setTimeout(done, 1200); }
    }
  }
  if (!customElements.get('hero-banner')) customElements.define('hero-banner', HeroBanner);

  /* ---------- hero: boutique floor (show clip controls) ----------
     The clip is silent and loops. Visitors can pause it and turn sound on.
     Anyone who asks their device for less motion gets it paused. */
  class HeroFloor extends HTMLElement {
    connectedCallback() {
      var video = this.querySelector('.show__media video');
      if (!video) return;
      var tile = this.querySelector('.floor__show');
      var pauseBtn = this.querySelector('[data-show-pause]');
      var soundBtn = this.querySelector('[data-show-sound]');
      var setPaused = function (paused) {
        if (tile) tile.classList.toggle('is-paused', paused);
        if (pauseBtn) {
          pauseBtn.classList.toggle('is-paused', paused);
          pauseBtn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
        }
      };
      var tryPlay = function () {
        var started = video.play();
        if (started && started.catch) started.catch(function () { setPaused(true); });
      };
      video.muted = true;
      video.addEventListener('play', function () { setPaused(false); });
      video.addEventListener('pause', function () { setPaused(true); });
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        video.removeAttribute('autoplay');
        video.pause();
        setPaused(true);
      } else {
        tryPlay();
      }
      if (pauseBtn) pauseBtn.addEventListener('click', function () {
        if (video.paused) tryPlay();
        else video.pause();
      });
      if (soundBtn) soundBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        var on = !video.muted;
        soundBtn.classList.toggle('is-on', on);
        soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on && video.paused) tryPlay();
      });
    }
  }
  if (!customElements.get('hero-floor')) customElements.define('hero-floor', HeroFloor);

  class LakeBand extends HTMLElement {}
  if (!customElements.get('lake-band')) customElements.define('lake-band', LakeBand);

  /* ---------- product rail (carousel) ---------- */
  class ProductRail extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = this.querySelector('[data-rail-track]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.bar = this.querySelector('[data-rail-bar]');
      if (!this.track) return;
      var step = function () { return self.track.clientWidth * 0.82; };
      if (this.prev) this.prev.addEventListener('click', function () { self.track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
      if (this.next) this.next.addEventListener('click', function () { self.track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
    update() {
      var t = this.track, max = t.scrollWidth - t.clientWidth;
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft >= max - 2;
      if (this.bar) {
        var p = t.scrollWidth ? t.clientWidth / t.scrollWidth : 1;
        var pos = max > 0 ? t.scrollLeft / max : 0;
        this.bar.style.setProperty('--p', Math.min(100, p * 100) + '%');
        this.bar.style.setProperty('--x', (pos * (1 / Math.max(p, 0.0001) - 1) * 100) + '%');
        this.bar.parentElement.style.visibility = p >= 0.999 ? 'hidden' : 'visible';
      }
    }
  }
  if (!customElements.get('product-rail')) customElements.define('product-rail', ProductRail);

  /* ---------- product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = this.querySelector('[data-track]');
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb]', this);
      this.counter = this.querySelector('[data-counter]');
      if (!this.track) return;
      this.track.addEventListener('scroll', debounce(function () { self.sync(); }, 60), { passive: true });
      this.thumbs.forEach(function (b) { b.addEventListener('click', function () { self.go(parseInt(b.getAttribute('data-thumb'), 10)); }); });
      var prev = this.querySelector('[data-prev]'), next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); });
      if (next) next.addEventListener('click', function () { self.go(self.index + 1); });
      this.track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(self.index - 1); }
      });
      this.index = 0;
      // Lightbox
      this.lightbox = this.closest('product-info') ? this.closest('product-info').querySelector('[data-lightbox]') : null;
      var openLb = function (i) { self.openLightbox(i); };
      var zoomBtn = this.querySelector('[data-open-lightbox]');
      if (zoomBtn) zoomBtn.addEventListener('click', function () { openLb(self.index); });
      this.slides.forEach(function (s, i) { var img = s.querySelector('img'); if (img) img.addEventListener('click', function () { openLb(i); }); });
      if (this.lightbox) {
        this.lightbox.addEventListener('click', function (e) { if (e.target.closest('[data-close-lightbox]')) self.closeLightbox(); });
        this.lightbox.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.closeLightbox(); });
      }
    }
    go(i) {
      var n = this.slides.length;
      if (!n) return;
      i = (i + n) % n;
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.setIndex(i);
    }
    goToMedia(id) {
      var self = this;
      this.slides.forEach(function (s, i) { if (s.getAttribute('data-media-id') === String(id)) self.go(i); });
    }
    sync() {
      var w = this.track.clientWidth || 1;
      this.setIndex(Math.round(this.track.scrollLeft / w));
    }
    setIndex(i) {
      this.index = i;
      if (this.counter) this.counter.textContent = i + 1;
      this.thumbs.forEach(function (t, k) { t.setAttribute('aria-current', k === i ? 'true' : 'false'); });
    }
    openLightbox(i) {
      if (!this.lightbox) return;
      var slide = this.slides[i];
      var id = slide ? slide.getAttribute('data-media-id') : null;
      this.lightbox.classList.add('is-open');
      lock();
      var target = id ? this.lightbox.querySelector('[data-media-id="' + id + '"]') : null;
      var lbTrack = this.lightbox.querySelector('[data-lightbox-track]');
      if (target && lbTrack) lbTrack.scrollLeft = target.offsetLeft;
      $$('img', this.lightbox).forEach(function (img) { img.loading = 'eager'; });
      this.lightbox.focus();
    }
    closeLightbox() {
      if (!this.lightbox || !this.lightbox.classList.contains('is-open')) return;
      this.lightbox.classList.remove('is-open');
      unlock();
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---------- product info: variants, add to bag, sticky bar ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      var self = this;
      var json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.form = this.querySelector('[data-product-form]');
      this.idInput = this.querySelector('[data-variant-input]');
      this.addBtn = this.querySelector('[data-add]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.errorEl = this.querySelector('[data-error]');
      this.priceWrap = this.querySelector('[data-price-wrap]');
      this.gallery = this.querySelector('product-gallery');
      this.sticky = this.querySelector('[data-sticky]');
      this.stickyPrice = this.querySelector('[data-sticky-price]');
      this.sizeIndex = parseInt(this.getAttribute('data-size-index'), 10) || 0;

      var opts = this.querySelector('[data-options]');
      if (opts) opts.addEventListener('change', function () { self.onOptionChange(); });
      if (this.form) this.form.addEventListener('submit', function (e) { self.onSubmit(e); });

      this.addEventListener('click', function (e) {
        var a = e.target.closest('[data-open-acc]');
        if (!a) return;
        e.preventDefault();
        var d = document.getElementById(a.getAttribute('data-open-acc'));
        if (d) { d.open = true; d.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' }); }
      });

      // Sticky bar shows once the main buy buttons scroll out of view
      var buy = this.querySelector('[data-buy]');
      if (buy && this.sticky) {
        var ticking = false;
        var check = function () {
          ticking = false;
          var show = buy.getBoundingClientRect().bottom < 0;
          if (show !== self.sticky.classList.contains('is-visible')) {
            self.sticky.classList.toggle('is-visible', show);
            self.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          }
        };
        this.onStickyScroll = function () { if (!ticking) { ticking = true; requestAnimationFrame(check); } };
        window.addEventListener('scroll', this.onStickyScroll, { passive: true });
        check();
      }

      this.updateAvailability();
      this.highlightSize();
      this.loadRecommendations();
      track('view_item', { product: (this.getAttribute('data-url') || '').split('/').pop() });
    }
    disconnectedCallback() { if (this.onStickyScroll) window.removeEventListener('scroll', this.onStickyScroll); }
    selectedOptions() {
      return $$('[data-option-index]', this).map(function (fs) {
        var c = fs.querySelector('input:checked');
        return c ? c.value : null;
      });
    }
    findVariant(options) {
      return this.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === options[i]; });
      });
    }
    onOptionChange() {
      var options = this.selectedOptions();
      $$('[data-option-index]', this).forEach(function (fs, i) {
        var lbl = fs.querySelector('[data-selected-label]');
        if (lbl) lbl.textContent = options[i] || '';
      });
      var v = this.findVariant(options);
      this.updateAvailability();
      this.highlightSize();
      if (!v) { this.setButton(false, 'Unavailable'); return; }
      this.current = v;
      if (this.idInput) this.idInput.value = v.id;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      this.renderPrice(v);
      this.setButton(v.available, v.available ? 'Add to bag · ' + formatMoney(v.price) : 'Sold out');
      if (v.featured_media && this.gallery) this.gallery.goToMedia(v.featured_media.id);
      if (this.stickyPrice) this.stickyPrice.textContent = formatMoney(v.price) + (v.title && v.title !== 'Default Title' ? ' · ' + v.title : '');
      if (this.errorEl) this.errorEl.hidden = true;
    }
    renderPrice(v) {
      if (!this.priceWrap) return;
      var sale = v.compare_at_price && v.compare_at_price > v.price;
      this.priceWrap.innerHTML = '<span class="price' + (sale ? ' price--sale' : '') + '"><span class="price__now">' + formatMoney(v.price) + '</span>' +
        (sale ? '<s><span class="visually-hidden">Was </span>' + formatMoney(v.compare_at_price) + '</s>' : '') + '</span>';
    }
    setButton(enabled, label) {
      if (!this.addBtn) return;
      this.addBtn.disabled = !enabled;
      if (this.addLabel) this.addLabel.textContent = label;
    }
    updateAvailability() {
      var self = this;
      var selected = this.selectedOptions();
      $$('[data-option-index]', this).forEach(function (fs, idx) {
        $$('.opt__value', fs).forEach(function (wrap) {
          var input = wrap.querySelector('input');
          var test = selected.slice();
          test[idx] = input.value;
          var ok = self.variants.some(function (v) {
            return v.available && v.options.every(function (o, i) { return i === idx ? o === input.value : (test[i] == null || o === test[i]); });
          });
          wrap.classList.toggle('is-unavailable', !ok);
        });
      });
    }
    highlightSize() {
      if (!this.sizeIndex) return;
      var size = this.selectedOptions()[this.sizeIndex - 1];
      $$('[data-measure] tr[data-size]', this).forEach(function (tr) {
        tr.classList.toggle('is-active', tr.getAttribute('data-size') === size);
      });
    }
    onSubmit(e) {
      e.preventDefault();
      var self = this;
      if (!this.addBtn || this.addBtn.disabled) return;
      this.addBtn.classList.add('is-loading');
      if (this.errorEl) this.errorEl.hidden = true;
      var fd = new FormData(this.form);
      fd.append('sections', 'cart-drawer');
      fd.append('sections_url', window.location.pathname);
      fetch((routes.cartAdd || '/cart/add') + '.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          self.addBtn.classList.remove('is-loading');
          if (!res.ok || res.data.status) {
            if (self.errorEl) { self.errorEl.textContent = res.data.description || res.data.message || 'Sorry, that could not be added.'; self.errorEl.hidden = false; }
            return;
          }
          var drawer = $('#CartDrawer');
          if (drawer && res.data.sections && res.data.sections['cart-drawer']) {
            drawer.render(res.data.sections['cart-drawer']);
            drawer.open(self.addBtn);
          } else {
            window.location.href = routes.cart || '/cart';
          }
          track('add_to_cart', { product: res.data.handle || '', variant_id: res.data.variant_id || '', value: (res.data.final_line_price || 0) / 100 });
        })
        .catch(function () {
          self.addBtn.classList.remove('is-loading');
          self.form.submit();
        });
    }
    loadRecommendations() {
      var recs = $('product-recs');
      if (recs && recs.load) recs.load();
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /* ---------- product recommendations ---------- */
  class ProductRecs extends HTMLElement {
    connectedCallback() {
      var self = this;
      if (this.children.length && this.querySelector('.rail')) return; // already rendered (editor)
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); self.load(); } }, { rootMargin: '0px 0px 600px 0px' });
        io.observe(this);
      } else { this.load(); }
    }
    load() {
      var self = this;
      if (this.loading || this.loaded) return;
      this.loading = true;
      var url = this.getAttribute('data-url');
      if (!url) return;
      var max = parseInt(this.getAttribute('data-max'), 10) || 8;
      var current = this.getAttribute('data-handle') || '';
      var line = this.getAttribute('data-line') || '';
      var handleOf = function (li) {
        var a = li.querySelector('a[href*="/products/"]');
        return a ? a.getAttribute('href').split('?')[0].split('/products/')[1] : '';
      };
      var show = function (fresh) {
        var trackEl = fresh && fresh.querySelector('[data-rail-track]');
        if (trackEl && trackEl.children.length) {
          self.innerHTML = fresh.innerHTML;
          initReveal(self);
        } else {
          self.hidden = true;
        }
        self.loaded = true;
      };
      fetch(url).then(function (r) { return r.text(); }).then(function (html) {
        var fresh = parseHTML(html).querySelector('product-recs');
        var trackEl = fresh && fresh.querySelector('[data-rail-track]');
        if (!trackEl || trackEl.children.length >= max) return show(fresh);
        BrandIndex.load().then(function (items) {
          var used = {}; used[current] = 1;
          var recs = $$('li', trackEl);
          recs.forEach(function (li) { used[handleOf(li)] = 1; });
          var sameLine = line ? items.filter(function (it) { return !used[it.handle] && it.tags.indexOf(' ' + line + ' ') > -1; }) : [];
          sameLine.forEach(function (it) { used[it.handle] = 1; });
          var rest = items.filter(function (it) { return !used[it.handle]; });
          var nodes = sameLine.map(function (it) { return it.el; }).concat(recs, rest.map(function (it) { return it.el; })).slice(0, max);
          trackEl.innerHTML = '';
          nodes.forEach(function (node) {
            var li = trackEl.ownerDocument.createElement('li');
            li.innerHTML = node.innerHTML;
            trackEl.appendChild(li);
          });
          show(fresh);
        }).catch(function () { show(fresh); });
      }).catch(function () { self.hidden = true; });
    }
  }
  if (!customElements.get('product-recs')) customElements.define('product-recs', ProductRecs);

  /* ---------- collection: sort + load more ---------- */
  function initCollection(root) {
    root = root || document;
    $$('[data-sort]', root).forEach(function (sel) {
      sel.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sel.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
    $$('[data-load-more]', root).forEach(function (wrap) {
      var btn = wrap.querySelector('[data-next-url]');
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var next = btn.getAttribute('data-next-url');
        if (!next) return;
        btn.classList.add('is-loading');
        fetch(next).then(function (r) { return r.text(); }).then(function (html) {
          var doc = parseHTML(html);
          var grid = $('[data-grid]', root);
          var items = $$('[data-grid] > li', doc);
          items.forEach(function (li) { grid.appendChild(document.importNode(li, true)); });
          initReveal(grid);
          var fresh = doc.querySelector('[data-load-more]');
          if (fresh) {
            wrap.innerHTML = fresh.innerHTML;
            initCollection(wrap.parentElement);
          } else { wrap.remove(); }
          var url = new URL(next, window.location.origin);
          window.history.replaceState({}, '', url.pathname + url.search);
        }).catch(function () { window.location.href = next; });
      });
    });
    $$('[data-cart-page]', root).forEach(function (form) {
      var btn = form.querySelector('[data-update-btn]');
      $$('[data-cart-qty]', form).forEach(function (input) {
        input.addEventListener('change', debounce(function () { if (btn) btn.click(); }, 400));
      });
    });
  }

  /* ---------- boot ---------- */
  function initSearchPage(root) {
    var box = $('[data-search-page]', root);
    if (!box) return;
    var q = box.getAttribute('data-q') || '';
    if (!q.trim()) return;
    var serverCount = $$('.card', box).length;
    BrandIndex.load().then(function () {
      var hits = BrandIndex.search(q);
      if (!hits.length || hits.length < serverCount) return;
      box.innerHTML = '<h2 class="visually-hidden">Products</h2><ul class="pgrid pgrid--4" role="list">' +
        hits.map(function (h) { return '<li>' + h.el.innerHTML + '</li>'; }).join('') + '</ul>';
      initReveal(box);
    }).catch(function () {});
  }

  function boot(root) {
    initReveal(root);
    initCollection(root);
    initSearchPage(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) {
    var hdr = e.target.querySelector('site-header');
    if (hdr) hdr.classList.add('is-scrolled');
  });
})();
